import { useEffect, useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCampaignStore } from '../stores/campaignStore';
import { campaignService } from '../services/campaignService';
import { useVoucherStore } from '../stores/voucherStore';
import { trackVoucherReservation } from '../integrations/insider/insider.events';
import { StepProgress } from '../components/StepProgress';
import { CampaignHeader } from '../components/CampaignHeader';
import { useAnalytics } from '../hooks/useAnalytics';

interface AvailableDate {
  value: string;
  month: string;
  day: string;
  dateLabel: string;
  weekday: string;
  disabled: boolean;
  reason?: string;
}

const MONTHS = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const WEEKDAYS = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

function formatAvailableDate(value: string, disabled = false, reason?: string): AvailableDate {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return {
    value,
    month: MONTHS[month - 1],
    day: String(day),
    dateLabel: `${day}/${MONTHS[month - 1].toLowerCase()}`,
    weekday: WEEKDAYS[date.getDay()],
    disabled,
    reason,
  };
}

export function Step3SelectDate() {
  const navigate = useNavigate();
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const { campaign } = useCampaignStore();
  const { reservation, selectDate, setCurrentStep, updateReservation } = useVoucherStore();
  const { trackException, trackSelectContent } = useAnalytics();
  const [selectedDate, setSelectedDate] = useState(reservation?.scheduledDate || '');
  const [dates, setDates] = useState<AvailableDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAvailability = async () => {
    if (!campaign?.id || !reservation?.storeId) {
      setLoading(false);
      setError('Não foi possível identificar a campanha ou a loja selecionada.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const availability = await campaignService.getStoreAvailability(campaign.id, reservation.storeId);
      const availableDates = availability.availableDates.map((d) => formatAvailableDate(d, false));
      const disabledDates = (availability.disabledDates || []).map((d) => formatAvailableDate(d.date, true, d.reason));
      const allDates = [...availableDates, ...disabledDates].sort((a, b) => a.value.localeCompare(b.value));
      setDates(allDates);
      if (selectedDate && !availability.availableDates.includes(selectedDate)) {
        setSelectedDate('');
      }
    } catch {
      trackException('date_availability_load_failed');
      setDates([]);
      setError('Não foi possível carregar as datas disponíveis. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAvailability();
  }, [campaign?.id, reservation?.storeId]);

  const handleSelectDate = (date: AvailableDate) => {
    trackSelectContent('redemption_date', date.value);
    setSelectedDate(date.value);
    selectDate(date.value);
  };

  const [submitting, setSubmitting] = useState(false);

  const handleContinue = async () => {
    if (!selectedDate || !campaign?.id || !reservation?.storeId) return;

    setSubmitting(true);
    setError('');
    try {
      const { data } = await (await import('../services/api')).apiClient.post('/customer/reservations', {
        campaignId: campaign.id,
        storeId: reservation.storeId,
        redemptionDate: selectedDate,
      });

      // Store reservation result in voucher store
      const store = campaign.stores?.find((s) => s.id === reservation.storeId);
      updateReservation({
        ...reservation,
        scheduledDate: selectedDate,
        voucherCode: data.voucherCode,
        qrCodeToken: data.qrCodeToken,
        storeName: store?.name || reservation.storeName || '',
        storeCity: store?.city || '',
        storeState: store?.state || '',
      });

      setCurrentStep(4);
      // Insider: track reservation event
      trackVoucherReservation({
        voucher_id: data.voucherCode,
        reservation_date: new Date().toISOString().slice(0, 10),
        redemption_date: selectedDate,
        redemption_datetime: selectedDate,
        store_code: reservation.storeCode || store?.code || '',
        store_name: store?.name || reservation.storeName || '',
        campaign_name: campaign?.name || '',
      });
      navigate(`/${campaignSlug}/confirmacao`);
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { message?: string; detail?: string } } };
      trackException(`reservation_failed_${error.response?.status || 'unknown'}`);
      if (error.response?.status === 409) {
        setError('Você já possui uma reserva ativa para esta campanha.');
      } else if (error.response?.status === 422) {
        setError(error.response.data?.message || 'Data selecionada não está mais disponível.');
      } else {
        setError('Erro ao realizar reserva. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CampaignHeader />

      <div className="max-w-lg mx-auto w-full px-6 pt-6">
        <StepProgress currentStep={3} />
      </div>

      <main className="max-w-lg mx-auto w-full px-6 pb-6 flex-1">
        <p className="text-sm text-gray-600 mb-9">Selecione a data que irá resgatar seu brinde</p>

        {loading && (
          <div className="flex justify-center py-16" aria-live="polite">
            <Loader2 size={28} className="animate-spin text-rchlo-green" />
          </div>
        )}

        {!loading && error && (
          <div className="py-10 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button type="button" onClick={() => void loadAvailability()} className="btn-primary mt-5">
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !error && dates.length === 0 && (
          <p className="py-10 text-center text-sm text-gray-500">
            Não há datas disponíveis para esta loja.
          </p>
        )}

        {!loading && !error && dates.length > 0 && <div className="space-y-5">
          {dates.map((date) => {
            const isSelected = selectedDate === date.value;
            const isDisabled = date.disabled;
            return (
              <button
                key={date.value}
                type="button"
                onClick={() => !isDisabled && handleSelectDate(date)}
                disabled={isDisabled}
                className={`w-full rounded-2xl border p-5 text-left transition-all ${
                  isDisabled
                    ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                    : isSelected
                      ? 'border-rchlo-green bg-[#eff7f2] ring-1 ring-rchlo-green'
                      : 'border-gray-200 bg-gray-50/70'
                }`}
              >
                <span className="flex items-center gap-5">
                  <span className={`flex h-[70px] w-[70px] shrink-0 flex-col items-center justify-center rounded-2xl ${
                    isDisabled
                      ? 'bg-gray-300 text-gray-500'
                      : isSelected
                        ? 'bg-rchlo-green text-white'
                        : 'bg-gray-200 text-gray-700'
                  }`}>
                    <span className={`text-sm ${isDisabled ? 'text-gray-500' : isSelected ? 'text-white' : 'text-gray-500'}`}>{date.month}</span>
                    <span className="text-3xl font-bold leading-8">{date.day}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-lg font-bold ${isDisabled ? 'text-gray-400' : isSelected ? 'text-rchlo-green' : 'text-gray-900'}`}>{date.dateLabel}</span>
                    <span className="block text-base text-gray-500">{date.weekday}</span>
                    {isDisabled && date.reason && (
                      <span className="block text-xs text-red-400 mt-1">{date.reason}</span>
                    )}
                  </span>
                  {isSelected && !isDisabled && <CheckCircle size={25} className="shrink-0 fill-rchlo-green text-white" aria-label="Data selecionada" />}
                </span>
              </button>
            );
          })}
        </div>}
      </main>

      <footer className="max-w-lg mx-auto w-full px-6 pb-6 space-y-2">
        <button onClick={() => void handleContinue()} disabled={!selectedDate || submitting} className="btn-primary w-full">
          {submitting ? 'Reservando...' : 'Próximo'}
        </button>
        <button onClick={() => navigate(`/${campaignSlug}/lojas`)} disabled={submitting} className="btn-secondary w-full">
          Voltar
        </button>
      </footer>
    </div>
  );
}

